import React from 'react';
import { ClockIcon, MapPinIcon } from 'lucide-react';
import { doodleCatalog } from '../../data/doodleShapes';
import type { WelcomeWeekDay } from '../../data/welcomeWeek';

/**
 * One night of Welcome Week. Each day gets its own accent band and corner shape
 * so the five cards feel like a set rather than five identical boxes.
 *
 * A pending location is shown as a deliberate dashed chip — never hidden, and
 * never filled in with a guessed room.
 */

const accents = [{
  band: 'bg-brand-blue',
  shape: doodleCatalog.navy.stars[3]
}, {
  band: 'bg-brand-yellow',
  shape: doodleCatalog.navy.organics[2]
}, {
  band: 'bg-brand-tan',
  shape: doodleCatalog.navy.stars[8]
}, {
  band: 'bg-brand-navy',
  shape: doodleCatalog.yellow.stars[5]
}, {
  band: 'bg-brand-pink',
  shape: doodleCatalog.navy.organics[7]
}];
export function WelcomeWeekCard({
  event,
  index,
  featured = false
}: {
  event: WelcomeWeekDay;
  index: number;
  featured?: boolean;
}) {
  const accent = accents[index % accents.length];
  const onDarkBand = accent.band === 'bg-brand-navy';
  return <article className="flex h-full flex-col overflow-hidden rounded-[20px] border-2 border-brand-navy bg-white transition-transform duration-200 hover:-translate-y-1" data-id="element-395" data-mp="fc158s9dkz5u">
      {/* The band carries no shape: it is a small strip and the date sits in the
          only corner a shape could occupy. The accent moves to the card body,
          well clear of the day, date, time, and location. */}
      <div className={`relative flex items-baseline justify-between gap-3 px-5 py-3 ${accent.band}`} data-id="element-396" data-mp="z4631r4nwsu7">
        <p className={`font-display text-lg font-extrabold leading-none ${onDarkBand ? 'text-white' : 'text-brand-navy'}`} data-id="element-397" data-mp="pca3scquawoe">
          {event.day}
        </p>
        <p className={`relative font-meta text-[13px] font-semibold ${onDarkBand ? 'text-white' : 'text-brand-navy'}`} data-id="element-398" data-mp="1hsktac75o2ty">
          {event.date}
        </p>
      </div>

      <div className={`relative flex flex-1 flex-col p-5 ${featured ? 'sm:p-6' : ''}`} data-id="element-399" data-mp="379gdg6fq8gl5">
        <img src={accent.shape} alt="" aria-hidden="true" draggable={false} className="pointer-events-none absolute bottom-3 right-3 h-7 w-7 rotate-12 object-contain opacity-25" data-id="element-400" data-mp="3bbbpo5z74edp" />
        <h3 className={`font-display font-bold leading-snug text-brand-navy ${featured ? 'text-xl sm:text-2xl' : 'text-lg'}`} data-id="element-401" data-mp="32hymmb7f3wdw">
          {event.title}
        </h3>

        <dl className="mt-3 space-y-2" data-id="element-402" data-mp="2smn6ppk9wj4k">
          <div className="flex items-start gap-2" data-id="element-403" data-mp="2elarxhgumnk9">
            <dt className="sr-only" data-id="element-404" data-mp="2emm4a5od13mz">Time</dt>
            <ClockIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-navy" data-id="element-405" data-mp="2jkdy558keztt" />
            <dd className="font-meta text-[14px] font-semibold text-brand-navy" data-id="element-406" data-mp="2jm8h4e0a0hv9">{event.time}</dd>
          </div>

          <div className="flex items-start gap-2" data-id="element-407" data-mp="hsr3mtpgsmsl">
            <dt className="sr-only" data-id="element-408" data-mp="hsir52x97jhx">Location</dt>
            <MapPinIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-navy" data-id="element-409" data-mp="rwpm2n4jpjmw" />
            <dd className="font-sans text-[14px] leading-snug text-ink" data-id="element-410" data-mp="my4okvgohe25">
              {event.location ? event.location : <span className="inline-flex rounded-full border border-dashed border-brand-navy px-2.5 py-1 font-meta text-[12.5px] font-semibold text-brand-navy" data-id="element-411" data-mp="10qkj17vik3rg">
                  {event.locationPending}
                </span>}
            </dd>
          </div>
        </dl>

        {event.callout ? <p className="mt-3 inline-flex w-fit rounded-full bg-brand-yellow px-3 py-1 font-meta text-[12.5px] font-semibold text-[#3d2b00]" data-id="element-412" data-mp="15qqcmtlcg0c6">
            {event.callout}
          </p> : null}

        <p className="mt-3 font-sans text-[14.5px] leading-relaxed text-ink" data-id="element-413" data-mp="1fkpfxvx5lq5p">{event.description}</p>
      </div>
    </article>;
}